import { SessionTimelineSegmentReordered } from '@lifeway/frontend-analytics';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-scroll';
import {
  SortableContainer,
  SortableContainerProps,
  SortableElement,
  SortableElementProps,
  SortableHandle,
} from 'react-sortable-hoc';
import styled from 'styled-components';
import { Breakpoints, Color } from '../../../app/StyleGuide';
import { InlineRadioList, InlineRadioListItem } from '../../../components/FormElements';
import { PageTitle } from '../../../components/Layout';
import alertService from '../../../services/AlertService';
import sessionTimelineService from '../../../services/sessionTimelineService';
import { handleError } from '../../../utils/apiUtils';
import { formatSegmentReorderedEvent, useAnalytics } from '../../analytics';
import { useUser } from '../../authentication';
import { useScrollUpDetection } from '../hooks/useScrollUpDetection';
import useSessionTimeline from '../hooks/useSessionTimeline';
import { Segment } from '../models/timeline';
import { SessionTimelineProvider } from '../state/SessionTimelineContext';
import PrintPreferenceModal from './PrintPreferenceModal';
import { SegmentPillNav } from './SegmentPillNav';
import SessionTimelineContent from './SessionTimelineContent';
import { SectionLabel } from './SessionTimelineContentElements';
import {
  DurationIcon,
  SessionAgenda,
  SessionLayout,
  SessionMenu,
  SessionMenuBackdrop,
  SessionMenuButtons,
  SessionMenuContents,
  SessionMenuHeader,
  SessionMenuListItem,
  SessionTimelineContainer,
  SessionTimelineHeader,
  SessionTimelinePreferences,
} from './SessionTimelineElements';

const SegmentGroupIconMap = Object.freeze({
  leaders: 'fas fa-chalkboard-teacher',
  small: 'fas fa-user-friends',
  large: 'fas fa-users',
});

const MobileDiv = styled.div`
  @media (min-width: ${Breakpoints.screen_md}) {
    display: none;
  }

  @media print {
    display: none;
  }
`;

type ContainerProps = SortableContainerProps & {
  children: React.ReactNode;
};

type ItemProps = SortableElementProps & {
  children: React.ReactNode;
  className?: string;
};

const ReorderableSessionMenuContents = SortableContainer<ContainerProps>(({ children }: ContainerProps) => (
  <SessionMenuContents>{children}</SessionMenuContents>
));

const ReorderableSessionMenuItem = SortableElement<ItemProps>(({ children, className, index }: ItemProps) => (
  <SessionMenuListItem className={className}>{children}</SessionMenuListItem>
));

const OrderingDragHandle = SortableHandle(() => (
  <i data-qa-hook="menuDragIcon" className="fas fa-bars drag-handle"></i>
));

type SessionTimelinePresenterProps = {
  title: React.ReactNode;
  subtitle: string;
  customizable?: boolean;
  useViewPreferences: boolean;
  actionButtons: React.ReactNode;
  config?: { stickyOffset: number; scrollContainerId: string; topOffset: number };
};

const SessionTimelinePresenter = ({
  title,
  subtitle,
  customizable = false,
  useViewPreferences,
  actionButtons,
  config,
}: SessionTimelinePresenterProps) => {
  const { user } = useUser();
  const {
    session,
    timeline,
    setTimeline,
    viewPreferenceMap,
    segmentGroupMap,
    segmentLeaderMap,
    updateLeaderViewPreference,
  } = useSessionTimeline();
  const analytics = useAnalytics();

  const [showPrintModal, setShowPrintModal] = useState(false);
  const [isSlideMenuOpen, setIsSlideMenuOpen] = useState(false);
  const menuRef = useRef<HTMLElement>(null);

  const { stickyOffset = 60, scrollContainerId = '', topOffset = 60 } = config ?? {};

  const showPillNav = useScrollUpDetection({
    scrollContainerId: scrollContainerId || undefined,
    thresholdSelector: '.segment-header',
  });

  useEffect(() => {
    if (isSlideMenuOpen && menuRef.current) {
      const activeItem = menuRef.current.querySelector('.active');
      if (activeItem) {
        activeItem.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
    }
  }, [isSlideMenuOpen]);

  useEffect(() => {
    if (isSlideMenuOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isSlideMenuOpen]);

  const showGroupHeader = (currentGroup: string, i: number) => currentGroup !== timeline.segments[i - 1].group;

  const getIndexFromSortableIndex = (sortableIndex: number) => sortableIndex / 10;

  const getSortableIndex = (segmentIndex: number, isHeader = false) =>
    isHeader ? segmentIndex * 10 - 5 : segmentIndex * 10;

  const didChangeOrder = (oldIndex: number, newIndex: number) => Math.abs(oldIndex - newIndex) >= 10;

  const reorderSegments = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    if (!didChangeOrder(oldIndex, newIndex)) return;

    const fromIndex = getIndexFromSortableIndex(oldIndex);
    const toIndex = getIndexFromSortableIndex(newIndex);

    const [reordered, original] = sessionTimelineService.changeOrderOfSegment(timeline, fromIndex, toIndex);

    setTimeline(previousTimeline => sessionTimelineService.reorderSegmentsInTimeline(previousTimeline, reordered));

    sessionTimelineService
      .customizeSegmentOrder(
        timeline.id,
        timeline.originalTimelineId,
        user?.lastSelectedAccount,
        reordered.map(s => s.segmentId),
        user?.userId
      )
      .then(() => {
        const segment = reordered.find(s => s.segmentId === timeline.segments[toIndex].segmentId);
        if (segment) {
          analytics.emit(
            new SessionTimelineSegmentReordered({
              curriculumId: undefined,
              ...formatSegmentReorderedEvent(session, segment, fromIndex, toIndex),
            })
          );
        }

        alertService.show('Leader Guide Updated');
        setTimeline(previousTimeline => sessionTimelineService.clearTimelineArchiveId(previousTimeline));
      })
      .catch(error => {
        handleError(error);
        setTimeline(previousTimeline => sessionTimelineService.reorderSegmentsInTimeline(previousTimeline, original));
      });
  };

  const shouldShowSegment = (segment: Segment) =>
    !useViewPreferences || sessionTimelineService.matchesLeaderViewPreference(timeline.viewPreference, segment.leader);

  return (
    <>
      <SessionLayout stickyOffset={stickyOffset} topOffset={topOffset}>
        <SessionAgenda>
          <div className="session-agenda__title">
            <h3>Agenda</h3>
            <p>
              <i className="fas fa-clock"></i> {timeline.duration} Minutes
            </p>
          </div>
          <ol className="session-agenda__list">
            {timeline.segments?.map((segment, i, segments) => (
              <li key={segment.segmentId} className="session-agenda__list-item">
                {i > 0 && segment.group !== segments[i - 1].group && (
                  <div className="session-agenda__list-item__transition">
                    <i className={SegmentGroupIconMap[segment.group as keyof typeof SegmentGroupIconMap]} />
                    <div>
                      <h5>{segmentGroupMap[segment.group as keyof typeof segmentGroupMap]}</h5>
                      <p>
                        Transition from {segmentGroupMap[segments[i - 1].group as keyof typeof segmentGroupMap]} to{' '}
                        {segmentGroupMap[segment.group as keyof typeof segmentGroupMap]}
                      </p>
                    </div>
                  </div>
                )}
                <div className="session-agenda__list-item__segment">
                  <div>
                    <h5>
                      {i + 1} - {segment.name}
                    </h5>
                    <p>{segmentLeaderMap[segment.leader as keyof typeof segmentLeaderMap]}</p>
                  </div>
                  <span>
                    <i className="fas fa-clock"></i> {segment.duration} Minutes
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </SessionAgenda>

        <SessionMenu ref={menuRef} mobileOpen={isSlideMenuOpen}>
          <SessionMenuButtons>{actionButtons}</SessionMenuButtons>
          <SessionMenuHeader>
            <h3 className="no-margin-top">Leader Guide Contents</h3>
            <p data-qa-hook="timelineDuration-Menu">
              <i className="fas fa-clock"></i>
              {` ${timeline.duration} Minutes`}
            </p>
          </SessionMenuHeader>

          <ReorderableSessionMenuContents onSortEnd={reorderSegments} useDragHandle>
            {!!timeline.segments.length &&
              timeline.segments.map((segment, i) => (
                <React.Fragment key={`timeline-menu-contents-${i}`}>
                  {(i === 0 || showGroupHeader(segment.group, i)) && (
                    <ReorderableSessionMenuItem index={getSortableIndex(i, true)}>
                      <Link
                        containerId={scrollContainerId}
                        activeClass={i === 0 ? '' : 'active'}
                        className="group indented"
                        name={i === 0 ? `contents-${segment.segmentId}` : `transition-${i}`}
                        to={i === 0 ? `contents-${segment.segmentId}` : `transition-${i}`}
                        spy={i !== 0}
                        smooth={true}
                        duration={500}
                        offset={stickyOffset * -1}
                        onClick={() => setIsSlideMenuOpen(false)}
                      >
                        <i className={SegmentGroupIconMap[segment.group as keyof typeof SegmentGroupIconMap]} />{' '}
                        {segmentGroupMap[segment.group as keyof typeof segmentGroupMap]}
                      </Link>
                    </ReorderableSessionMenuItem>
                  )}
                  {shouldShowSegment(segment) && (
                    <ReorderableSessionMenuItem
                      index={getSortableIndex(i)}
                      className={!segment.enabled ? 'excluded-segment' : ''}
                    >
                      <Link
                        data-qa-hook={`contentMenuSegment-${i}`}
                        containerId={scrollContainerId}
                        activeClass="active"
                        className="indented"
                        name={`contents-${segment.segmentId}`}
                        to={`contents-${segment.segmentId}`}
                        spy={true}
                        smooth={true}
                        duration={500}
                        offset={stickyOffset * -1}
                        onClick={() => setIsSlideMenuOpen(false)}
                      >
                        {segment.name}
                      </Link>
                      {customizable && <OrderingDragHandle />}
                    </ReorderableSessionMenuItem>
                  )}
                </React.Fragment>
              ))}
          </ReorderableSessionMenuContents>
        </SessionMenu>
        <SessionMenuBackdrop open={isSlideMenuOpen} onClick={() => setIsSlideMenuOpen(false)} />
        <SessionTimelineContainer>
          <SessionTimelineHeader className={!useViewPreferences ? 'mb-48' : ''}>
            <PageTitle>
              <h2>{title}</h2>
              <p>{subtitle}</p>
            </PageTitle>
            <div>
              <DurationIcon duration={timeline.duration} />
            </div>
          </SessionTimelineHeader>
          {useViewPreferences && (
            <SessionTimelinePreferences className="mt-24 mb-32">
              <SectionLabel>Preferences</SectionLabel>
              <InlineRadioList>
                {Object.entries(viewPreferenceMap).map(([key, value]) => (
                  <InlineRadioListItem
                    key={key}
                    title={value.title}
                    description={value.description}
                    checkedColor={Color.Blue._50}
                    checked={timeline.viewPreference === key}
                    onChange={() => updateLeaderViewPreference(key)}
                  />
                ))}
              </InlineRadioList>
            </SessionTimelinePreferences>
          )}
          <SessionTimelineContent customizable={customizable} useViewPreferences={useViewPreferences} />
        </SessionTimelineContainer>
      </SessionLayout>
      {showPrintModal && (
        <PrintPreferenceModal
          leaderPreference={timeline.viewPreference ?? ''}
          handleDismiss={() => setShowPrintModal(false)}
        />
      )}
      <MobileDiv>
        <SegmentPillNav
          onClick={() => setIsSlideMenuOpen(true)}
          visible={showPillNav}
          title={'Leader Content'}
          data-tracking-id="open-leader-guide-content"
        />
      </MobileDiv>
    </>
  );
};

type SessionTimelineContainerProps = {
  curriculumName: string;
  timeline: any;
  session: any;
  children?: React.ReactNode;
} & SessionTimelinePresenterProps;

const SessionTimeline = ({
  curriculumName,
  timeline,
  session,
  title,
  subtitle,
  customizable,
  useViewPreferences,
  actionButtons,
  config,
  children,
}: SessionTimelineContainerProps) => {
  return (
    <SessionTimelineProvider curriculumName={curriculumName} timeline={timeline} session={session}>
      {children}
      <SessionTimelinePresenter
        title={title}
        subtitle={subtitle}
        customizable={customizable}
        useViewPreferences={useViewPreferences}
        actionButtons={actionButtons}
        config={config}
      />
    </SessionTimelineProvider>
  );
};

export default SessionTimeline;
